import * as ReactDOM from 'react-dom';
import useEventCallback from '@mui/utils/useEventCallback';
import useTimeout from '@mui/utils/useTimeout';
import { PickerManager } from '../../../models';
import { UseFieldDOMGetters, UseFieldInternalProps } from './useField.types';
import { useFieldRootHandleKeyDown } from './useFieldRootHandleKeyDown';
import { UseFieldStateReturnValue } from './useFieldState';
import { getActiveElement } from '../../utils/utils';
import { UseFieldCharacterEditingReturnValue } from './useFieldCharacterEditing';
import { syncSelectionToDOM } from './syncSelectionToDOM';

/**
 * Generate the props to pass to the root element of the field.
 * It is not used by the non-accessible DOM structure (with an <input /> element for editing).
 * It should be used in the MUI accessible DOM structure and the Base UI implementation.
 * @param {UseFieldRootPropsParameters} parameters The parameters of the hook.
 * @returns {UseFieldRootPropsReturnValue} The props to forward to the root element of the field.
 */
export function useFieldRootProps(
  parameters: UseFieldRootPropsParameters,
): UseFieldRootPropsReturnValue {
  const {
    manager,
    focused,
    setFocused,
    domGetters,
    stateResponse,
    applyCharacterEditing,
    internalPropsWithDefaults,
    stateResponse: {
      // States and derived states
      parsedSelectedSections,
      sectionOrder,
      state,

      // Methods to update the states
      clearValue,
      setCharacterQuery,
      setSelectedSections,
      updateValueFromValueStr,
    },
    internalPropsWithDefaults: { disabled = false, readOnly = false },
  } = parameters;

  // TODO: Inline onContainerKeyDown once the old DOM structure is removed
  const handleKeyDown = useFieldRootHandleKeyDown({
    manager,
    internalPropsWithDefaults,
    stateResponse,
  });

  const containerClickTimeout = useTimeout();
  const handleClick = useEventCallback(() => {
    if (disabled || !domGetters.isReady()) {
      return;
    }

    if (parsedSelectedSections === 'all') {
      setFocused(true);
      containerClickTimeout.start(0, () => {
        // `getSelection`/`getRangeAt` can be unset transiently (e.g. focus
        // moved before this 0-tick callback ran). Fall back to the first
        // section in that case.
        const selection = document.getSelection();
        if (!selection || selection.rangeCount === 0) {
          setSelectedSections(sectionOrder.startIndex);
          return;
        }
        const cursorPosition = selection.getRangeAt(0).startOffset;

        if (cursorPosition === 0) {
          setSelectedSections(sectionOrder.startIndex);
          return;
        }

        let sectionIndex = 0;
        let cursorOnStartOfSection = 0;

        while (cursorOnStartOfSection < cursorPosition && sectionIndex < state.sections.length) {
          const section = state.sections[sectionIndex];
          sectionIndex += 1;
          cursorOnStartOfSection += `${section.startSeparator}${
            section.value || section.placeholder
          }${section.endSeparator}`.length;
        }

        setSelectedSections(sectionIndex - 1);
      });
      return;
    }

    // For trusted pointer input, `handleMouseDown` already ran and set the
    // section. This branch only matters for `click` events that arrive
    // without a preceding `mousedown` (programmatic `element.click()`, some
    // assistive-technology activations, synthetic event sequences in tests).
    // Fall back to the first section so the field doesn't enter a "focused
    // but no active section" state.
    if (!focused) {
      setFocused(true);
      if (parsedSelectedSections == null) {
        setSelectedSections(sectionOrder.startIndex);
      }
    }
  });

  // Replaces Chromium's focus delegation with an explicit section focus on
  // every primary mousedown inside the sections container. The CSS gate
  // (`WebkitUserModify: read-only` while not `:focus-within`) can make the
  // section's contenteditable span temporarily non-focusable, in which case
  // Chromium falls back to the sections-container `tabindex=0` and our
  // `handleFocus` then runs the "no active section" fallback, briefly
  // selecting the first section before the click bubble corrects it.
  // Setting the target section here (and letting `syncSelectionToDOM` move
  // focus via `.focus()`, which works even with the user-modify rule
  // active) skips that race entirely.
  const handleMouseDown = useEventCallback((event: React.MouseEvent) => {
    if (disabled || !domGetters.isReady() || parsedSelectedSections === 'all') {
      return;
    }
    if (event.button !== 0) {
      return;
    }
    const target = event.target as Element;
    const sectionListRoot = domGetters.getRoot();
    const isInsideSectionList = sectionListRoot.contains(target);
    // Only the field root is ours outside the sections container: it is the
    // target of a padding click. The adornments keep their own behavior.
    if (!isInsideSectionList && target !== event.currentTarget) {
      return;
    }
    const sectionElement = isInsideSectionList
      ? target.closest<HTMLElement>('[data-sectionindex]')
      : null;

    // Blank space is the field padding and the area past the sections. Mimic
    // the native date input: first section on entry, keep it afterwards.
    // `preventDefault` is what keeps it, by stopping both the browser blur and
    // Chromium's focus delegation.
    const isBlankSpaceClick =
      sectionElement == null &&
      (!isInsideSectionList || isPointOutsideSections(sectionListRoot, event.clientX));
    if (isBlankSpaceClick) {
      event.preventDefault();
      if (!focused) {
        // Flush so the layout effects focus the section on this same tick,
        // reading back whatever a controlled `selectedSections` accepted.
        ReactDOM.flushSync(() => {
          setFocused(true);
          setSelectedSections(sectionOrder.startIndex);
        });
      }
      return;
    }

    // Prefer the visually-containing section (matches Chromium's
    // delegation + section container `onClick`), fall back to the
    // closest-by-distance section for clicks on the container padding.
    const parsedIndex = sectionElement
      ? Number(sectionElement.dataset.sectionindex)
      : findClosestSectionIndexToPoint(sectionListRoot, event.clientX);
    // `Number(undefined) === NaN` and `NaN == null === false`, so guard
    // explicitly here even though `data-sectionindex` is set by
    // `useFieldSectionContainerProps` for every section in practice.
    if (parsedIndex == null || !Number.isInteger(parsedIndex)) {
      return;
    }
    event.preventDefault();
    // `mousedown` is now authoritative for pointer section selection. The
    // section container's own `onClick` deduplicates against the resulting
    // `parsedSelectedSections` on the click bubble (see
    // `useFieldSectionContainerProps`), so we always select here and let that
    // guard absorb the redundant call -- matching the pre-PR
    // `onSelectedSectionsChange` invocation count.
    // See the comment on the blank-space branch above for why this flushes.
    ReactDOM.flushSync(() => {
      setFocused(true);
      setSelectedSections(parsedIndex);
    });
  });

  const handleInput = useEventCallback((event: React.FormEvent<HTMLDivElement>) => {
    if (!domGetters.isReady() || parsedSelectedSections !== 'all') {
      return;
    }

    const target = event.target as HTMLSpanElement;
    const keyPressed = target.textContent ?? '';

    domGetters.getRoot().innerHTML = state.sections
      .map(
        (section) =>
          `${section.startSeparator}${section.value || section.placeholder}${section.endSeparator}`,
      )
      .join('');
    syncSelectionToDOM({ focused, domGetters, stateResponse });

    if (keyPressed.length === 0 || keyPressed.charCodeAt(0) === 10) {
      clearValue();
      setSelectedSections('all');
    } else if (keyPressed.length > 1) {
      updateValueFromValueStr(keyPressed);
    } else {
      if (parsedSelectedSections === 'all') {
        setSelectedSections(0);
      }
      applyCharacterEditing({
        keyPressed,
        sectionIndex: 0,
      });
    }
  });

  const handlePaste = useEventCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (readOnly || parsedSelectedSections !== 'all') {
      event.preventDefault();
      return;
    }

    const pastedValue = event.clipboardData.getData('text');
    event.preventDefault();
    setCharacterQuery(null);
    updateValueFromValueStr(pastedValue);
  });

  const handleFocus = useEventCallback(() => {
    if (focused || disabled || !domGetters.isReady()) {
      return;
    }

    const activeElement = getActiveElement(domGetters.getRoot());

    setFocused(true);

    const isFocusInsideASection = domGetters.getSectionIndexFromDOMElement(activeElement) != null;
    if (!isFocusInsideASection) {
      setSelectedSections(sectionOrder.startIndex);
    }
  });

  const handleBlur = useEventCallback(() => {
    setTimeout(() => {
      if (!domGetters.isReady()) {
        return;
      }

      const activeElement = getActiveElement(domGetters.getRoot());
      const shouldBlur = !domGetters.getRoot().contains(activeElement);
      if (shouldBlur) {
        setFocused(false);
        setSelectedSections(null);
      }
    });
  });

  return {
    // Event handlers
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onClick: handleClick,
    onMouseDown: handleMouseDown,
    onPaste: handlePaste,
    onInput: handleInput,

    // Other
    contentEditable: parsedSelectedSections === 'all',
    tabIndex: internalPropsWithDefaults.disabled || parsedSelectedSections === 0 ? -1 : 0, // TODO: Try to set to undefined when there is a section selected.
  };
}

/**
 * Slop on each side of the sections, so a click that just misses the outermost
 * one still selects it. Under half a digit at the default font size.
 */
const BLANK_SPACE_TOLERANCE = 4;

/**
 * Returns `true` when `clientX` sits past the sections on either side.
 * Horizontal only: a click in the container's vertical padding still belongs to
 * the section above or below it.
 */
function isPointOutsideSections(root: HTMLElement, clientX: number): boolean {
  const sections = root.querySelectorAll<HTMLElement>('[data-sectionindex]');
  if (sections.length === 0) {
    return false;
  }

  let left = Infinity;
  let right = -Infinity;
  for (let i = 0; i < sections.length; i += 1) {
    const rect = sections[i].getBoundingClientRect();
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
  }

  return clientX < left - BLANK_SPACE_TOLERANCE || clientX > right + BLANK_SPACE_TOLERANCE;
}

/**
 * Returns the index of the section whose horizontal center is closest to `clientX`.
 * Returns `null` if the field renders no `[role="spinbutton"]` descendants
 * (defensive -- every section content span sets `role="spinbutton"` in practice).
 */
function findClosestSectionIndexToPoint(root: HTMLElement, clientX: number): number | null {
  const sections = root.querySelectorAll<HTMLElement>('[role="spinbutton"]');
  if (sections.length === 0) {
    return null;
  }
  let closestIndex = 0;
  let closestDistance = Infinity;
  for (let i = 0; i < sections.length; i += 1) {
    const rect = sections[i].getBoundingClientRect();
    const center = (rect.left + rect.right) / 2;
    const distance = Math.abs(clientX - center);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = i;
    }
  }
  return closestIndex;
}

interface UseFieldRootPropsParameters {
  manager: PickerManager<any, any, any, any, any>;
  stateResponse: UseFieldStateReturnValue<any>;
  applyCharacterEditing: UseFieldCharacterEditingReturnValue;
  internalPropsWithDefaults: UseFieldInternalProps<any, any, any>;
  domGetters: UseFieldDOMGetters;
  focused: boolean;
  setFocused: (focused: boolean) => void;
}

interface UseFieldRootPropsReturnValue {
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  onBlur: React.FocusEventHandler<HTMLDivElement>;
  onFocus: React.FocusEventHandler<HTMLDivElement>;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onMouseDown: React.MouseEventHandler<HTMLDivElement>;
  onPaste: React.ClipboardEventHandler<HTMLDivElement>;
  onInput: React.FormEventHandler<HTMLDivElement>;
  contentEditable: boolean;
  tabIndex: number;
}
